import React, { useMemo, useState, useCallback } from "react";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useAdminUsers, AdminUser, AdminUserStatus } from "@/hooks/admin/useAdminUsers";
import { useBulkSelection } from "@/hooks/useBulkSelection";
import { UsersBulkActionsToolbar } from "./UsersBulkActionsToolbar";
import UsersDrawer from "@/components/admin/users/UsersDrawer";
import UsersTable from "@/components/admin/users/UsersTable";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { useQueryClient } from "@tanstack/react-query";

export function UsersExplorer() {
  const queryClient = useQueryClient();
  const { data: users = [], isLoading } = useAdminUsers();

  const [search, setSearch] = useState("");
  const [roleFilter, setRoleFilter] = useState<string>("all");
  const [statusFilter, setStatusFilter] = useState<AdminUserStatus | "all">("all");
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [selected, setSelected] = useState<AdminUser | null>(null);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return users.filter((u: AdminUser) => {
      const matchesSearch = !q || [u.name, u.email].some((v) => (v || "").toLowerCase().includes(q));
      const role = (u.role || "").toLowerCase();
      const roleMatchesFilter = (rf: string) => {
        if (rf === "sys_admin") return role === "sys_admin" || role === "admin"; // include legacy 'admin'
        if (rf === "manager") return role === "manager" || role === "manager_admin";
        return role === rf;
      };
      const matchesRole = roleFilter === "all" || roleMatchesFilter(roleFilter);
      const matchesStatus = statusFilter === "all" || u.status === statusFilter;
      return matchesSearch && matchesRole && matchesStatus;
    });
  }, [users, search, roleFilter, statusFilter]);

  const [bulkState, bulkActions] = useBulkSelection(filtered.length);

  const [page, setPage] = useState<number>(1);
  const [rowsPerPage, setRowsPerPage] = useState<number>(25);

  const onApprove = useCallback(async () => {
    const selectedIds = Array.from(bulkState.selectedIds);
    const selectedWaitlist = filtered.filter(u => selectedIds.includes(u.id) && u.source === "waitlist");
    if (selectedWaitlist.length === 0) {
      toast.info("Select waitlisted users to approve");
      return;
    }
    const waitlistIds = selectedWaitlist.map(u => u.id);
    const { error } = await supabase.functions.invoke("admin-users-manage", { body: { action: "approve_waitlist", waitlist_ids: waitlistIds } });
    if (error) {
      toast.error(error.message || "Approval failed");
      return;
    }
    toast.success("Invites sent");
    bulkActions.clearSelection();
    await queryClient.invalidateQueries({ queryKey: ["admin-users"] });
  }, [bulkState.selectedIds, filtered, bulkActions, queryClient]);
  const onActivate = useCallback(async () => {
    const selectedIds = Array.from(bulkState.selectedIds);
    const selectedProfiles = filtered.filter(u => selectedIds.includes(u.id) && u.source === "profile");
    if (selectedProfiles.length === 0) {
      toast.info("Select active users to activate");
      return;
    }
    const profileIds = selectedProfiles.map(u => u.id);
    const { error } = await supabase.functions.invoke("admin-users-manage", { body: { action: "activate", profile_ids: profileIds } });
    if (error) { toast.error(error.message || "Activate failed"); return; }
    toast.success("Users activated");
    bulkActions.clearSelection();
    await queryClient.invalidateQueries({ queryKey: ["admin-users"] });
  }, [bulkState.selectedIds, filtered, bulkActions, queryClient]);

  const onDeactivate = useCallback(async () => {
    const selectedIds = Array.from(bulkState.selectedIds);
    const selectedProfiles = filtered.filter(u => selectedIds.includes(u.id) && u.source === "profile");
    if (selectedProfiles.length === 0) {
      toast.info("Select users to deactivate");
      return;
    }
    const profileIds = selectedProfiles.map(u => u.id);
    const { error } = await supabase.functions.invoke("admin-users-manage", { body: { action: "deactivate", profile_ids: profileIds } });
    if (error) { toast.error(error.message || "Deactivate failed"); return; }
    toast.success("Users deactivated");
    bulkActions.clearSelection();
    await queryClient.invalidateQueries({ queryKey: ["admin-users"] });
  }, [bulkState.selectedIds, filtered, bulkActions, queryClient]);

  const onInvite = useCallback(async () => {
    const selectedIds = Array.from(bulkState.selectedIds);
    const selectedWaitlist = filtered.filter(u => selectedIds.includes(u.id) && u.source === "waitlist");
    if (selectedWaitlist.length === 0) { toast.info("Select waitlisted users to invite"); return; }
    const emails = selectedWaitlist.map(u => u.email).filter(Boolean);
    const { error } = await supabase.functions.invoke("admin-users-manage", { body: { action: "invite", emails } });
    if (error) { toast.error(error.message || "Invite failed"); return; }
    toast.success("Invites sent");
    bulkActions.clearSelection();
    await queryClient.invalidateQueries({ queryKey: ["admin-users"] });
  }, [bulkState.selectedIds, filtered, bulkActions, queryClient]);

  const onDelete = useCallback(async () => {
    const selectedIds = Array.from(bulkState.selectedIds);
    if (selectedIds.length === 0) { toast.info("Select users to delete"); return; }
    if (!window.confirm("Soft-delete selected users? They will be purged after 7 days.")) return;
    const selected = filtered.filter(u => selectedIds.includes(u.id));
    const profileIds = selected.filter(u => u.source === "profile").map(u => u.id);
    const waitlistIds = selected.filter(u => u.source === "waitlist").map(u => u.id);
    const { error } = await supabase.functions.invoke("admin-users-manage", { body: { action: "delete", profile_ids: profileIds, waitlist_ids: waitlistIds } });
    if (error) { toast.error(error.message || "Delete failed"); return; }
    toast.success("Users deleted (soft-delete)");
    bulkActions.clearSelection();
    await queryClient.invalidateQueries({ queryKey: ["admin-users"] });
  }, [bulkState.selectedIds, filtered, bulkActions, queryClient]);

  const start = (page - 1) * rowsPerPage;
  const paged = filtered.slice(start, start + rowsPerPage);

  const pageCount = Math.max(1, Math.ceil(filtered.length / rowsPerPage));

  return (
    <div className="space-y-4">
      <div className="flex flex-col sm:flex-row gap-3">
        <Input
          placeholder="Search users..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="min-h-[44px]"
        />
        <Select value={roleFilter} onValueChange={(v) => setRoleFilter(v)}>
          <SelectTrigger className="min-h-[44px] w-[180px]">
            <SelectValue placeholder="Role" />
          </SelectTrigger>
          <SelectContent className="z-50 bg-popover">
            <SelectItem value="all">All Roles</SelectItem>
            <SelectItem value="investor">Investor</SelectItem>
            <SelectItem value="advisor">Advisor</SelectItem>
            <SelectItem value="manager">Manager</SelectItem>
            <SelectItem value="sys_admin">System Admin</SelectItem>
          </SelectContent>
        </Select>
        <Select value={statusFilter} onValueChange={(v) => setStatusFilter(v as any)}>
          <SelectTrigger className="min-h-[44px] w-[180px]">
            <SelectValue placeholder="Status" />
          </SelectTrigger>
          <SelectContent className="z-50 bg-popover">
            <SelectItem value="all">All Statuses</SelectItem>
            <SelectItem value="active">Active</SelectItem>
            <SelectItem value="inactive">Inactive</SelectItem>
            <SelectItem value="waitlisted">Waitlisted</SelectItem>
            <SelectItem value="invited">Invited</SelectItem>
            <SelectItem value="pending_delete">Pending delete</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <UsersBulkActionsToolbar
        selectedCount={bulkState.selectedCount}
        totalCount={filtered.length}
        onClearSelection={bulkActions.clearSelection}
        onApprove={onApprove}
        onActivate={onActivate}
        onDeactivate={onDeactivate}
        onInvite={onInvite}
        onDelete={onDelete}
      />

      <UsersTable
        users={paged}
        allIdsOnPage={paged.map((u) => u.id)}
        totalCount={filtered.length}
        isLoading={isLoading}
        page={page}
        pageCount={pageCount}
        rowsPerPage={rowsPerPage}
        onPageChange={(p) => setPage(p)}
        onRowsPerPageChange={(n) => { setRowsPerPage(n); setPage(1); }}
        isSelected={bulkActions.isSelected}
        toggleItem={bulkActions.toggleItem}
        toggleAll={() => bulkActions.toggleAll(paged.map((u) => u.id))}
        onRowClick={(u) => { setSelected(u); setDrawerOpen(true); }}
      />
      <UsersDrawer open={drawerOpen} onOpenChange={setDrawerOpen} user={selected} />
    </div>
  );
}

export default UsersExplorer;
